import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchCart,
  fetchCartCount,
  mergeCart as mergeCartService,
  addToCart as addToCartService,
  updateCartQuantity as updateCartQuantityService,
  removeCartItem as removeCartItemService,
  clearCart as clearCartService,
  toggleCartItem as toggleCartItemService,
  toggleSelectAll as toggleSelectAllService,
  selectBySeller as selectBySellerService,
  applyDiscount as applyDiscountService,
  removeDiscount as removeDiscountService,
} from "./cart.service";
import {
  CartResponse,
  CartCountResponse,
  MergeCartResponse,
  AddToCartRequest,
  AddToCartResponse,
  UpdateCartQuantityRequest,
  UpdateCartQuantityResponse,
  RemoveCartItemResponse,
  ClearCartResponse,
  ToggleCartItemResponse,
  ToggleSelectAllRequest,
  ToggleSelectAllResponse,
  SelectBySellerRequest,
  SelectBySellerResponse,
  ApplyDiscountRequest,
  ApplyDiscountResponse,
  RemoveDiscountResponse,
} from "./cart.types";
import { handleApiError } from "@/app/utils/handleApiError";

/**
 * Fetch full cart data
 */
export const getCart = createAsyncThunk<
  CartResponse,
  void,
  { rejectValue: string }
>("cart/getCart", async (_, { rejectWithValue }) => {
  try {
    const response = await fetchCart();
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Fetch cart item count only
 */
export const getCartCount = createAsyncThunk<
  CartCountResponse,
  void,
  { rejectValue: string }
>("cart/getCartCount", async (_, { rejectWithValue }) => {
  try {
    const response = await fetchCartCount();
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Merge guest cart with user cart after login
 */
export const mergeCart = createAsyncThunk<
  MergeCartResponse,
  void,
  { rejectValue: string }
>("cart/mergeCart", async (_, { rejectWithValue }) => {
  try {
    const response = await mergeCartService();
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Add product to cart
 */
export const addToCart = createAsyncThunk<
  AddToCartResponse,
  AddToCartRequest,
  { rejectValue: string }
>("cart/addToCart", async (data, { rejectWithValue }) => {
  try {
    const response = await addToCartService(data);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Update cart item quantity
 */
export const updateCartQuantity = createAsyncThunk<
  UpdateCartQuantityResponse,
  { itemId: number; data: UpdateCartQuantityRequest },
  { rejectValue: string }
>(
  "cart/updateCartQuantity",
  async ({ itemId, data }, { rejectWithValue }) => {
    try {
      const response = await updateCartQuantityService(itemId, data);
      if (!response.status) {
        return rejectWithValue(response.message);
      }
      return response;
    } catch (error) {
      return rejectWithValue(handleApiError(error));
    }
  },
);

/**
 * Remove item from cart
 */
export const removeCartItem = createAsyncThunk<
  RemoveCartItemResponse,
  number,
  { rejectValue: string }
>("cart/removeCartItem", async (itemId, { rejectWithValue }) => {
  try {
    const response = await removeCartItemService(itemId);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Clear entire cart
 */
export const clearCartThunk = createAsyncThunk<
  ClearCartResponse,
  void,
  { rejectValue: string }
>("cart/clearCart", async (_, { rejectWithValue }) => {
  try {
    const response = await clearCartService();
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Toggle cart item selection
 */
export const toggleCartItem = createAsyncThunk<
  ToggleCartItemResponse,
  number,
  { rejectValue: string }
>("cart/toggleCartItem", async (itemId, { rejectWithValue }) => {
  try {
    const response = await toggleCartItemService(itemId);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Toggle selection of all cart items
 */
export const toggleSelectAll = createAsyncThunk<
  ToggleSelectAllResponse,
  ToggleSelectAllRequest,
  { rejectValue: string }
>("cart/toggleSelectAll", async (data, { rejectWithValue }) => {
  try {
    const response = await toggleSelectAllService(data);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Select/deselect all items from a specific seller
 */
export const selectBySeller = createAsyncThunk<
  SelectBySellerResponse,
  SelectBySellerRequest,
  { rejectValue: string }
>("cart/selectBySeller", async (data, { rejectWithValue }) => {
  try {
    const response = await selectBySellerService(data);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

/**
 * Apply discount code or coupon
 */
export const applyDiscount = createAsyncThunk<
  ApplyDiscountResponse,
  ApplyDiscountRequest,
  { rejectValue: string }
>("cart/applyDiscount", async (data, { rejectWithValue }) => {
  try {
    const response = await applyDiscountService(data);
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});

export const removeDiscount = createAsyncThunk<
  RemoveDiscountResponse,
  void,
  { rejectValue: string }
>("cart/removeDiscount", async (_, { rejectWithValue }) => {
  try {
    const response = await removeDiscountService();
    return response;
  } catch (error) {
    return rejectWithValue(handleApiError(error));
  }
});
